import type { FetchRequestArgs } from "./types";

/**
 * Appends nested values to FormData using Rails parameter conventions.
 *
 * Examples:
 * - appendFormDataValue(formData, "agent[name]", "QA")
 * - appendFormDataValue(formData, "agent[rulesets]", [{ name: "Legal" }])
 *
 * Rails parses those keys back into ActionController::Parameters, which lets
 * multipart endpoints keep the same nested param shape as JSON requests.
 */
export const appendFormDataValue = (formData: FormData, key: string, value: unknown): void => {
  if (value === undefined || value === null) {
    return;
  }

  if (value instanceof File) {
    formData.append(key, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => appendFormDataValue(formData, `${key}[]`, item));
    return;
  }

  if (typeof value === "object") {
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      appendFormDataValue(formData, `${key}[${nestedKey}]`, nestedValue);
    });
    return;
  }

  formData.append(key, String(value));
};

/**
 * Builds the JSON-vs-multipart request arguments for an endpoint that may
 * carry an optional `File`.
 *
 * When no file is present the payload is sent as a normal JSON request. When a
 * file is present every payload field is flattened into a `FormData` body using
 * Rails parameter conventions (see {@link appendFormDataValue}) and
 * `setContentType` is disabled so the browser can set the multipart boundary.
 *
 * @param payload the JSON payload to send (file-less fields)
 * @param file an optional file to attach under `fileKey`
 * @param fileKey the multipart field name for the file (default `"brief_file"`)
 * @param repeatedFiles an optional set of files to attach as repeated `key[]` parts, for an
 *   endpoint that takes a list of uploads alongside (or instead of) the single `fileKey` one.
 *   Rails reads `key[]` parts back as an array, which is why they are bracketed and `fileKey` is
 *   not. Either kind of file switches the request to multipart.
 */
export const buildFetchRequestArgs = <PayloadType extends object>(
  payload: PayloadType,
  file: File | null | undefined,
  fileKey = "brief_file",
  repeatedFiles?: { key: string; files: File[] }
): FetchRequestArgs<PayloadType> => {
  const { key: repeatedKey, files: repeated = [] } = repeatedFiles ?? { key: "", files: [] };
  if ((file === undefined || file === null) && repeated.length === 0) {
    return { params: payload, options: {}, setContentType: true };
  }

  const body = new FormData();
  Object.entries(payload).forEach(([key, value]) => appendFormDataValue(body, key, value));
  if (file !== undefined && file !== null) {
    body.append(fileKey, file);
  }
  repeated.forEach((repeatedFile) => body.append(`${repeatedKey}[]`, repeatedFile));

  return { params: {}, options: { body }, setContentType: false };
};

/**
 * Like {@link buildFetchRequestArgs}, but for endpoints whose payload is too deeply nested to
 * survive bracket-flattening (which is lossy for types, empty arrays, and deep nesting): the
 * whole payload ships as ONE JSON-encoded part under `jsonKey`, next to raw `fileKey[]` file
 * parts. The backend parses the JSON part back into params (see agent_definition_upsert's
 * agent_json handling; same workaround as AsyncCampaignQaController's campaign_document).
 *
 * An endpoint where only one nested SUBTREE of the payload is flattening-sensitive does not need
 * this whole-payload envelope: it JSON-encodes that one field's value itself and calls
 * {@link buildFetchRequestArgs} (see the agent preview's `agent_definition`, which the backend
 * accepts as an object or a JSON string under the same key).
 *
 * With no files the payload is sent as a normal JSON request, so callers get one entry point for
 * both shapes.
 *
 * @param payload the JSON payload to send
 * @param files the files to attach as repeated `fileKey[]` parts
 * @param jsonKey the multipart field name for the JSON-encoded payload
 * @param fileKey the multipart field name for the files (sent as `fileKey[]`)
 */
export const buildJsonPayloadFetchRequestArgs = <PayloadType extends object>(
  payload: PayloadType,
  files: File[] | null | undefined,
  jsonKey: string,
  fileKey: string
): FetchRequestArgs<PayloadType> => {
  if (!files || files.length === 0) {
    return { params: payload, options: {}, setContentType: true };
  }

  const body = new FormData();
  body.append(jsonKey, JSON.stringify(payload));
  files.forEach((file) => body.append(`${fileKey}[]`, file));

  return { params: {}, options: { body }, setContentType: false };
};
